import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Breadcrumb, Button, Divider } from 'antd';
import { NavLink, useSearchParams } from 'react-router-dom';

import styles from './index.module.scss';
import { IconBookmark, IconBookmarkCheck } from '../../../Common/Icon';
import { Article, useAddBookmark, useRemoveBookmark } from '../../../../hooks/api/article';
import { useArticleContext } from '../../../Common/Header/context';

export interface SearchCardProps {
  data?: Article;
  bookmarked?: boolean;
  refetch?: () => void;
}

export default function SearchCard({ data, refetch, bookmarked }: SearchCardProps): React.ReactNode | null {
  const { filters, setFilters, buildQueryString } = useArticleContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const addBookmark = useAddBookmark(data?.id);
  const removeBookmark = useRemoveBookmark(data?.id);

  const handleTagClick = (tag: string) => {
    const currentTags = filters?.tags ?? [];
    const updatedTags = currentTags.includes(tag) ? currentTags : [...currentTags, tag];

    const updatedFilters = {
      ...filters,
      tags: updatedTags,
    };

    setFilters?.(updatedFilters);

    const query = buildQueryString?.(searchParams.get('title') ?? '', updatedFilters);

    if (query) setSearchParams(query);
  };

  const bookmarkClick = async () => {
    if (data?.isBookmarkedByCurrentUser) {
      const response = await removeBookmark.fetch();

      if (response?.success !== false) {
        refetch?.();
      }
    } else {
      const response = await addBookmark.fetch();

      if (response) {
        refetch?.();
      }
    }
  };

  return (
    <>
      <div className={styles.cardWrapper}>
        <div className={styles.cardContent}>
          {data?.subCategory
            ? (
              <Breadcrumb
                items={[
                  {
                    title: <NavLink to="/" className={styles.breadcrumbLink}>Help portal</NavLink>,
                  },
                  {
                    title: (
                      // eslint-disable-next-line max-len
                      <NavLink to={`/product/${data?.product.id}/${data?.product.name}`} className={styles.breadcrumbLink}>
                        {data?.product.name}
                      </NavLink>
                    ),
                  },
                  {
                    title: (
                      <NavLink
                        // eslint-disable-next-line max-len
                        to={`/category/${data?.product.name}/${data?.product.id}/${data?.category.name}/${data?.category.id}`}
                        className={styles.breadcrumbLink}
                      >
                        {data?.category?.name || 'Category'}
                      </NavLink>
                    ),
                  },
                  {
                    title: (
                      <NavLink to="/" className={styles.breadcrumbLink}>{data?.subCategory?.name || ''}</NavLink>
                    ),
                  },
                ]}
              />
            )
            : (
              <Breadcrumb
                items={[
                  {
                    title: <NavLink to="/" className={styles.breadcrumbLink}>Help portal</NavLink>,
                  },
                  {
                    title: (
                      // eslint-disable-next-line max-len
                      <NavLink to={`/product/${data?.product.id}/${data?.product.name}`} className={styles.breadcrumbLink}>
                        {data?.product.name}
                      </NavLink>
                    ),
                  },
                  {
                    title: (
                      <NavLink
                        // eslint-disable-next-line max-len
                        to={`/category/${data?.product.name}/${data?.product.id}/${data?.category.name}/${data?.category.id}`}
                        className={styles.breadcrumbLink}
                      >
                        {data?.category?.name || 'Category'}
                      </NavLink>
                    ),
                  },
                ]}
              />
            )}
          <NavLink to={`/article/${data?.product.name}/${data?.product.id}/${data?.id}`}>
            <h3>{data?.title}</h3>
          </NavLink>
          <div
            className={styles.description}
            dangerouslySetInnerHTML={{
              __html: `
      <span>
        <span class="${styles.date}">${dayjs(data?.createdAt).format('DD/MM/YYYY')}:</span>
        ${data?.content || ''}
      </span>
    `,
            }}
          />
          {data?.tags
            ? (
              <div className={styles.tagsWrapper}>
                {data.tags.map((item) => (
                  <div key={item} className={styles.tags}>
                    <Button
                      className={styles.tagBtn}
                      onClick={() => handleTagClick(item!)}
                    >
                      {item}
                    </Button>
                  </div>
                ))}
              </div>
            )
            : null}
        </div>
        <div>
          <Button
            type="primary"
            className={styles.bookmarkBtn}
            loading={removeBookmark.loading || addBookmark.loading}
            icon={data?.isBookmarkedByCurrentUser
              ? <IconBookmarkCheck color="#284F66" />
              : <IconBookmark color="#284F66" />}
            onClick={bookmarkClick}
          />
        </div>
      </div>
      <Divider />
    </>
  );
}
